import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Chip,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Email, CheckCircle, Close, Send } from "@mui/icons-material";
import { motion } from "framer-motion";
import { trackEvent, trackConversion } from "../utils/analytics";

interface EmailCaptureProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (email: string) => void;
  title?: string;
  description?: string;
  placeholder?: string;
  buttonText?: string;
  showBenefits?: boolean;
}

interface EmailCaptureForm {
  email: string;
  firstName?: string;
  interests?: string[];
}

const EmailCapture: React.FC<EmailCaptureProps> = ({
  open,
  onClose,
  onSuccess,
  title = "Get Your Free Keep Kid Busy Starter Pack",
  description = "Join 15,000+ parents who are creating amazing memories with their kids. Get instant access to our best activities, tips, and weekly inspiration.",
  placeholder = "Enter your email address",
  buttonText = "Get Free Access",
  showBenefits = true,
}) => {
  const [formData, setFormData] = useState<EmailCaptureForm>({
    email: "",
    firstName: "",
    interests: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const benefits = [
    "🎯 50+ Age-Appropriate Activities",
    "📱 Mobile-Friendly Instructions",
    "🎨 Creative Project Ideas",
    "🏃‍♂️ Outdoor Adventure Guides",
    "🧪 Science Experiment Kits",
    "📚 Weekly Family Bonding Tips",
  ];

  const interestOptions = [
    "Sports & Games",
    "Arts & Crafts",
    "Cooking Together",
    "Outdoor Adventures",
    "Learning Activities",
    "Music & Dance",
  ];

  const handleInputChange = (field: keyof EmailCaptureForm, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInterestToggle = (interest: string) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests?.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...(prev.interests || []), interest],
    }));
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email) {
      setError("Please enter your email address");
      return;
    }

    if (!validateEmail(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Track form submission
      trackEvent("email_capture", {
        email: formData.email,
        firstName: formData.firstName,
        interests: formData.interests,
        source: "landing_page",
      });

      // Track conversion
      trackConversion("email_captured", {
        conversionType: "email_captured",
        properties: {
          email: formData.email,
          firstName: formData.firstName,
          interests: formData.interests?.length || 0,
        },
      });

      // Simulate API call (replace with actual email service integration)
      await new Promise((resolve) => setTimeout(resolve, 1500));

      setIsSuccess(true);

      // Call success callback
      if (onSuccess) {
        onSuccess(formData.email);
      }

      // Track successful conversion
      trackEvent("email_capture_success", {
        email: formData.email,
        interests: formData.interests,
      });
    } catch (error) {
      setError("Something went wrong. Please try again.");
      trackEvent("email_capture_error", {
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      trackEvent("email_capture_closed");
      onClose();
    }
  };

  const handleRetry = () => {
    setIsSuccess(false);
    setFormData({ email: "", firstName: "", interests: [] });
    setError(null);
  };

  if (isSuccess) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            color: "white",
          },
        }}
      >
        <DialogContent sx={{ p: 4, textAlign: "center" }}>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <CheckCircle sx={{ fontSize: 80, color: "white", mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Welcome to Keep Kid Busy! 🎉
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              Check your email for your free starter pack. We've sent you:
            </Typography>

            <Box sx={{ textAlign: "left", mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                ✅ Welcome email with your first 5 activities
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                ✅ Weekly family bonding newsletter
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                ✅ Special access to premium content
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
              Can't find the email? Check your spam folder.
            </Typography>
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: "center" }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: "white",
              color: "white",
              "&:hover": {
                borderColor: "white",
                bgcolor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleRetry}
            variant="contained"
            sx={{
              bgcolor: "white",
              color: "#10b981",
              "&:hover": {
                bgcolor: "#f8fafc",
              },
            }}
          >
            Add Another Email
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          textAlign: "center",
          background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
          color: "white",
          borderRadius: "12px 12px 0 0",
          position: "relative",
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "white",
            minWidth: "auto",
            p: 1,
          }}
        >
          <Close />
        </Button>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {description}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "grid", gap: 3 }}>
            {/* Email Input */}
            <Box>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                Email Address *
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder={placeholder}
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                error={!!error}
                helperText={error}
                InputProps={{
                  startAdornment: (
                    <Email sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>

            {/* First Name Input */}
            <Box>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                First Name (Optional)
              </Typography>
              <TextField
                fullWidth
                placeholder="Your first name"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>

            {/* Interests Selection */}
            <Box>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                What interests you most? (Optional)
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {interestOptions.map((interest) => (
                  <Chip
                    key={interest}
                    label={interest}
                    onClick={() => handleInterestToggle(interest)}
                    variant={
                      formData.interests?.includes(interest)
                        ? "filled"
                        : "outlined"
                    }
                    color={
                      formData.interests?.includes(interest)
                        ? "primary"
                        : "default"
                    }
                    sx={{ borderRadius: 2 }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Benefits */}
            {showBenefits && (
              <Box
                sx={{
                  p: 3,
                  bgcolor: "rgba(59, 130, 246, 0.1)",
                  borderRadius: 2,
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 600, color: "#1e3a8a" }}
                >
                  What You'll Get:
                </Typography>
                <Box sx={{ display: "grid", gap: 1 }}>
                  {benefits.map((benefit, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{ opacity: 0.8 }}
                    >
                      {benefit}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* Privacy Notice */}
            <Typography
              variant="caption"
              sx={{ opacity: 0.7, textAlign: "center" }}
            >
              🔒 We respect your privacy. Unsubscribe at any time. No spam,
              ever.
            </Typography>
          </Box>
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: "center" }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          Maybe Later
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !formData.email}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <Send />}
          sx={{
            bgcolor: "#f97316",
            color: "white",
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
            "&:hover": {
              bgcolor: "#ea580c",
            },
            "&:disabled": {
              bgcolor: "#9ca3af",
            },
          }}
        >
          {isSubmitting ? "Submitting..." : buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailCapture;
